/**
 * Models
 */
export const GadgetsWarehouseFilter = (model: IGadgetsWarehouseFilter): IGadgetsWarehouseFilterModel => {
	const { id, name, is_external_supplier, partner_id } = model

	return {
		id,
		name,
		partnerId: partner_id,
		isExternalSupplier: is_external_supplier,
	}
}

const GadgetsWarehouseStatusStockWarehouse = (
	model: IGadgetsWarehouseStatusStockWarehouse,
): IGadgetsWarehouseStatusStockWarehouseModel => {
	const { id, uuid, name, is_external_supplier, gadget_partner_id } = model

	return {
		id,
		uuid,
		name,
		isExternalSupplier: is_external_supplier,
		gadgetPartnerId: gadget_partner_id,
	}
}

const GadgetsWarehouseStatusStock = (model: IGadgetsWarehouseStatusStock): IGadgetsWarehouseStatusStockModel => {
	const {
		id,
		uuid,
		gadget_id,
		gadget_variant_id,
		quantity,
		quantity_reserved,
		quantity_remaining,
		localization,
		stock_status,
		source,
		warehouse,
	} = model

	return {
		id,
		uuid,
		quantity,
		source,
		localization,
		gadgetId: gadget_id,
		gadgetVariantId: gadget_variant_id,
		quantityReserved: quantity_reserved,
		quantityRemaining: quantity_remaining,
		stockStatus: stock_status,
		warehouse: GadgetsWarehouseStatusStockWarehouse(warehouse),
	}
}

const GadgetsWarehouseStatusVariant = (model: IGadgetsWarehouseStatusVariant): IGadgetsWarehouseStatusVariantModel => {
	const { id, uuid, ean, is_active, image, attributes, statuses } = model

	return {
		id,
		uuid,
		ean,
		isActive: is_active,
		image: image ? Media(image) : null,
		attributes: attributes.map(GadgetsProductVariantAttribute),
		statuses: statuses ? statuses.map(GadgetsWarehouseStatusStock) : [],
	}
}

export const GadgetsWarehouseStatus = (model: IGadgetsWarehouseStatus): IGadgetsWarehouseStatusModel => {
	const {
		name,
		has_variants,
		is_active,
		name_trans,
		partner_id,
		partner,
		main_image,
		shipping_method,
		statuses,
		variants,
		...rest
	} = model

	return {
		...rest,
		name: safeLangContentMapper(name),
		nameTrans: name_trans,
		hasVariants: has_variants,
		isActive: is_active,
		shippingMethod: shipping_method,
		partnerId: partner_id,
		mainImage: main_image ? Media(main_image) : null,
		partner: partner ? GadgetsPartner(partner) : null,
		statuses: statuses ? statuses.map(GadgetsWarehouseStatusStock) : [],
		variants: variants ? variants.map(GadgetsWarehouseStatusVariant) : [],
	}
}

/**
 * Forms
 */
const GadgetsWarehouseStatusStockModelToForm = (
	model: IGadgetsWarehouseStatusStockModel,
	order: number,
): IGadgetsWarehouseStatusGadgetStatusForm => {
	const { id, localization, quantityReserved, quantityRemaining, warehouse } = model

	return {
		id,
		order,
		localization,
		quantityReserved,
		quantity: quantityRemaining,
		warehouseId: warehouse?.id,
		variantId: null,
	}
}

const GadgetsWarehouseStatusVariantModelItemToForm = (
	variant: IGadgetsWarehouseStatusVariantModel,
	variantOrderGroupsById: Record<number, number>,
): IGadgetsWarehouseStatusGadgetStatusForm[] => {
	const { id, statuses } = variant

	const models = statuses
		? statuses.map(status => {
				const order = variantOrderGroupsById[status.warehouse.id]

				return GadgetsWarehouseStatusStockModelToForm(status, order)
			})
		: []

	return [...models.map(model => ({ ...model, variantId: id }))]
}

const GadgetsWarehouseStatusVariantModelToForm = (
	variants: IGadgetsWarehouseStatusVariantModel[],
): IGadgetsWarehouseStatusGadgetStatusForm[] => {
	if (!variants) return []

	const ordersGroupsById: Record<number, number> = {}

	let order = 1
	variants
		.flatMap(variant => variant.statuses ?? [])
		.forEach(status => {
			const warehouseId = status.warehouse?.id

			if (!warehouseId) return

			if (warehouseId in ordersGroupsById) return

			ordersGroupsById[warehouseId] = order
			order++
		})

	return variants.flatMap(variant => GadgetsWarehouseStatusVariantModelItemToForm(variant, ordersGroupsById))
}

export const GadgetsWarehouseStatusModelToForm = (
	model: IGadgetsWarehouseStatusModel,
): IGadgetsWarehouseStatusGadgetStatusForm[] => {
	const { statuses, variants } = model

	let order = 1
	const statusesFromModel = statuses
		? statuses.flatMap(status => {
				order++

				return GadgetsWarehouseStatusStockModelToForm(status, order)
			})
		: []

	return [...statusesFromModel, ...(variants ? GadgetsWarehouseStatusVariantModelToForm(variants) : [])]
}

/**
 * Payloads
 */
const GadgetsWarehouseStatusStockPayload = (
	model: IGadgetsWarehouseStatusGadgetStatusForm,
): IGadgetsWarehouseStatusStockPayload => {
	const { id, warehouseId, variantId, localization, quantity, quantityReserved } = model

	let quentityForPayload = Number(quantity!)

	if (quantityReserved) {
		quentityForPayload = quentityForPayload + Number(quantityReserved)
	}

	return {
		id,
		localization,
		quantity: quentityForPayload,
		gadget_variant_id: variantId,
		gadget_warehouse_id: warehouseId!,
	}
}

export const GadgetsWarehouseStatusToPayload = (
	updated: IGadgetsWarehouseStatusModel,
	statuses: IGadgetsWarehouseStatusGadgetStatusForm[],
	initialStatuses: IGadgetsWarehouseStatusGadgetStatusForm[],
): IGadgetsWarehouseStatusPayload => {
	const deletedStatuses = initialStatuses
		.filter(status => !statuses.some(s => s.id === status.id))
		.map(status => status.id)
		.filter(id => id !== undefined && id !== null)

	return {
		gadget_id: updated.id,
		statuses: statuses ? statuses.map(GadgetsWarehouseStatusStockPayload) : [],
		delete_statuses: deletedStatuses,
	}
}
